import { useState } from "react";
import { Wand2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";

/** 重写指令上限(api `RewriteRequest.instruction` 的 `@Size(max = 200)`)。 */
export const REWRITE_INSTRUCTION_MAX = 200;

/**
 * 「AI 重写」小弹层:一句指令(默认「更短」)→ 重写 → 回值直接进框(不落库,保存草稿才写)。
 * 按钮的读屏名带字段名(「AI 重写:主标题」),一页十几个同名按钮时才分得清。
 */
export function RewriteButton({
  fieldLabel,
  onRewrite,
}: {
  fieldLabel: string;
  /** 回 true = 已把新值写进去(弹层关掉)。 */
  onRewrite: (instruction: string) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [instruction, setInstruction] = useState("更短");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      if (await onRewrite(instruction.trim())) setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={`AI 重写:${fieldLabel}`}
          className="h-6 px-1.5 text-2xs"
        >
          <Wand2 className="h-3 w-3" />
          AI 重写
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 space-y-2 p-3">
        <p className="text-xs font-medium text-foreground">怎么改?</p>
        <Input
          aria-label="重写指令"
          value={instruction}
          disabled={busy}
          maxLength={REWRITE_INSTRUCTION_MAX}
          className="h-8 text-sm"
          onChange={(e) => setInstruction(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && instruction.trim() && !busy) void run();
          }}
        />
        <Button
          type="button"
          size="sm"
          className="w-full"
          disabled={busy || instruction.trim() === ""}
          onClick={() => void run()}
        >
          重写
        </Button>
      </PopoverContent>
    </Popover>
  );
}
